import axios from "axios";
import { toast } from "sonner";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AUTH_KEY = "ws_auth";

// Some browsers block web storage entirely (private/incognito mode, Safari
// "Block all cookies", strict corporate privacy policies) or throw on write
// when the quota is full. Reads AND writes can throw. These helpers never throw;
// an in-memory fallback keeps the current session working even when persistent
// storage is unavailable, so a login can never fail just because of storage.
let _memAuth = null;
const safeGet = (store, key) => {
  try { return store.getItem(key); } catch { return null; }
};
const safeSet = (store, key, val) => {
  try { store.setItem(key, val); return true; } catch { return false; }
};
const safeRemove = (store, key) => {
  try { store.removeItem(key); } catch { /* storage unavailable */ }
};
// Generic never-throwing storage helpers for non-auth keys (badges, reload guards).
export const storageGet = (store, key) => safeGet(store, key);
export const storageSet = (store, key, val) => safeSet(store, key, val);

export const getAuth = () => {
  const raw = safeGet(localStorage, AUTH_KEY) || safeGet(sessionStorage, AUTH_KEY);
  if (raw) {
    try { return JSON.parse(raw); } catch { /* fall through to memory */ }
  }
  return _memAuth;
};
export const setAuth = (a, remember = true) => {
  _memAuth = a; // always retain in memory so the session works even if storage is blocked
  const raw = JSON.stringify(a);
  if (remember) {
    safeSet(localStorage, AUTH_KEY, raw);
    safeRemove(sessionStorage, AUTH_KEY);
  } else {
    safeSet(sessionStorage, AUTH_KEY, raw);
    safeRemove(localStorage, AUTH_KEY);
  }
};
export const clearAuth = () => {
  _memAuth = null;
  safeRemove(localStorage, AUTH_KEY);
  safeRemove(sessionStorage, AUTH_KEY);
};

// Merge a patch into the stored auth.profile, preserving its storage location.
export const patchStoredAuthProfile = (patch) => {
  const inLocal = safeGet(localStorage, AUTH_KEY);
  const raw = inLocal || safeGet(sessionStorage, AUTH_KEY) || (_memAuth ? JSON.stringify(_memAuth) : null);
  if (!raw) return null;
  let a;
  try { a = JSON.parse(raw); } catch { return null; }
  const next = { ...a, profile: { ...a.profile, ...patch } };
  _memAuth = next;
  safeSet(inLocal ? localStorage : sessionStorage, AUTH_KEY, JSON.stringify(next));
  return next;
};

// 60s default so a hung request can never spin forever; uploads (FormData) and
// file downloads (blob) are exempt because they legitimately take longer.
export const api = axios.create({ baseURL: API, timeout: 60000 });
api.interceptors.request.use((config) => {
  const a = getAuth();
  if (a?.token) config.headers.Authorization = `Bearer ${a.token}`;
  if (config.data instanceof FormData || config.responseType === "blob") config.timeout = 0;
  // Offline: reads may be served from the device cache by the service worker; writes need a connection.
  if (typeof navigator !== "undefined" && navigator.onLine === false && (config.method || "get").toLowerCase() !== "get") {
    const err = new axios.Cancel("offline");
    err.response = { status: 0, data: { detail: "You're offline — this action needs a connection. It will work again once you're back online." } };
    return Promise.reject(err);
  }
  return config;
});

// Session-expiry handling: if the upstream returns 401 while we DO hold a token
// (and the failing call is not an auth attempt), the JWT has expired — clear it
// and bounce to /login instead of surfacing a raw "Request failed 401" error.
let redirecting = false;
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const url = err.config?.url || "";
    const isAuthEndpoint = /\/(login|signup|verify-otp)/.test(url);
    if (err.code === "ECONNABORTED" && err.response == null) {
      err.response = { status: 0, data: { detail: "The server is taking too long to respond. Please try again." } };
    }
    if (err.response?.status === 401 && getAuth() && !isAuthEndpoint) {
      clearAuth();
      if (!redirecting && window.location.pathname !== "/login") {
        redirecting = true;
        toast.error("Your session has expired. Please sign in again.");
        setTimeout(() => {
          window.location.href = "/login";
        }, 400);
      }
    }
    return Promise.reject(err);
  }
);

// Build an absolute URL for <img>/download/anchor links (token in query string,
// since img/anchor cannot send Authorization headers). Matches the existing
// backend's `?token=<jwt>` content rule.
export const fileUrl = (path) => {
  const a = getAuth();
  const sep = path.includes("?") ? "&" : "?";
  return `${API}/${path}${a?.token ? `${sep}token=${encodeURIComponent(a.token)}` : ""}`;
};

export const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

// ---- role / permission helpers ----
export const isAdmin = (auth) => auth?.kind === "admin";
export const isAgent = (auth) => auth?.kind === "agent";
export const permOf = (auth) => auth?.profile?.permission;
export const isMaster = (auth) => auth?.profile?.role === "master";

// ---- department (informational) + granular per-staff rights ----
export const DEPARTMENTS = ["Front Office", "House Keeping", "Kitchen", "Security", "Store", "Management", "Food and Beverage", "Accounts"];
export const deptOf = (auth) => (auth?.profile?.department || "").toLowerCase().trim();
export const isManagement = (auth) => deptOf(auth) === "management";
export const rightsOf = (auth) => auth?.profile?.rights || [];
export const hasRight = (auth, key) => isAdmin(auth) && (isMaster(auth) || rightsOf(auth).includes(key));
export const canAssignRights = (auth) => isAdmin(auth) && (isMaster(auth) || isManagement(auth));

export const canEditOccupancy = (auth) => hasRight(auth, "occupancy.edit");
export const canEditBookings = (auth) => hasRight(auth, "bookings.edit");
export const canUploadContent = (auth) => hasRight(auth, "marketing.edit");
export const canEditRates = (auth) => hasRight(auth, "rates.edit");
export const canUseQuotes = (auth) => hasRight(auth, "quotes.use");
export const canViewWebCheckins = (auth) => hasRight(auth, "webcheckin.view");
export const canApprovePayments = (auth) => hasRight(auth, "payments.approve");
export const canManageUjhi = (auth) => hasRight(auth, "ujhi.manage");
export const canViewAgents = (auth) => hasRight(auth, "agents.view");
export const canChangeAgentRates = (auth) => hasRight(auth, "agents.rate");

// Format any YYYY-MM-DD / ISO date as "05 Aug 2026" — used across all portal date displays.
export const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(String(d).slice(0, 10) + "T00:00:00");
  if (isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};
export const canManage = (auth) => isAdmin(auth) && permOf(auth) === "full";
// Acquisition Analysis — Central Office Full permission, Management dept, or master only.
export const canAcquisition = (auth) => isAdmin(auth) && (permOf(auth) === "full" || isManagement(auth) || isMaster(auth));
export const allowedProps = (auth) => auth?.profile?.allowedProperties ?? null;

export const RIGHT_MODULES = [
  { key: "occupancy", label: "Occupancy", actions: ["view", "edit"] },
  { key: "bookings", label: "Bookings", actions: ["view", "edit"] },
  { key: "marketing", label: "Marketing", actions: ["view", "edit"] },
  { key: "promotions", label: "Promotions", actions: ["view", "edit"] },
  { key: "rates", label: "Rate Sheets", actions: ["view", "edit"] },
  { key: "quotes", label: "Create Quote", actions: ["use"] },
  { key: "webcheckin", label: "Web Check-in", actions: ["view"] },
  { key: "payments", label: "Payments", actions: ["approve"] },
  { key: "ujhi", label: "UJHI Villas (PMS)", actions: ["manage"] },
  { key: "agents", label: "Agents", actions: ["view", "rate"] },
  { key: "store", label: "Store & Purchase", actions: ["view", "edit"] },
];

// --- global transfer progress (pub/sub) : downloads + uploads ---
const _dlListeners = new Set();
export const onDownloadProgress = (cb) => { _dlListeners.add(cb); return () => _dlListeners.delete(cb); };
let _dlActive = 0;
const _emitDownload = (state) => _dlListeners.forEach((cb) => cb(state));
const _startProg = (label, mode) => { _dlActive += 1; _emitDownload({ active: true, percent: null, label, mode }); };
const _tickProg = (e, label, mode) => { const pct = e.total ? Math.round((e.loaded / e.total) * 100) : null; _emitDownload({ active: true, percent: pct, label, mode }); };
const _endProg = (label, mode) => { _emitDownload({ active: true, percent: 100, label, mode }); _dlActive = Math.max(0, _dlActive - 1); setTimeout(() => { if (_dlActive === 0) _emitDownload({ active: false, percent: 0, label: "", mode }); }, 500); };

// Upload with the same global progress bar. Returns the axios response.
export const uploadWithProgress = async (path, body, { label = "", method = "post", ...config } = {}) => {
  _startProg(label, "up");
  try {
    return await api.request({ url: path, method, data: body, onUploadProgress: (e) => _tickProg(e, label, "up"), ...config });
  } finally {
    _endProg(label, "up");
  }
};

// Authenticated file download (sends Authorization header; no token in the URL).
export const downloadFile = async (path, filename) => {
  const bust = `${path.includes("?") ? "&" : "?"}_=${Date.now()}`;
  _startProg(filename, "down");
  try {
    const res = await api.get(path + bust, {
      responseType: "blob",
      headers: { "Cache-Control": "no-cache" },
      onDownloadProgress: (e) => _tickProg(e, filename, "down"),
    });
    const url = window.URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } finally {
    _endProg(filename, "down");
  }
};
